import React, { useEffect, useMemo, useState } from 'react';
import { Box, Container, Typography, Button, CircularProgress, Alert, Chip } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import Navbar from '../../components/Navbar';
import Footer from '../../components/Footer';
import { useAuth } from '../../context/AuthContext';
import { getPublicTeam, getPublicDivision, listPublicTeamGames, listPublicTeamMatchups } from '../../services/publicProgramService';
import { hasProgramManagementAuthorityForTeam } from '../../services/programService';
import { listTeamStaff } from '../../services/teamService';
import { competitionTypeLabel, competitionTypeChipStyle } from '../../utils/divisionDisplay';
import { todayDateString } from '../../utils/teamSchedule';
import {
  gameStatusChipStyle, deriveGameResult, deriveTeamRecord,
  formatGameOpponentLine, formatGameScoreLine, formatGameDateCompact,
} from '../../utils/gameResult';

// Phase C.3 — the real public Team page
// (`/programs/:programSlug/teams/:teamId`). Team id (UUID), not slug —
// kept from the C.2 shell deliberately. Team slug (036) is optional and
// only ever set via update_team_public_page_settings(); requiring it for
// routing would mean a Team a Program Admin has correctly published
// (public_page_enabled = true) could still have no reachable URL if they
// never separately typed a slug — a worse outcome than a longer id in
// the address bar. Route correctness over aesthetics, per this phase's
// own instruction; revisiting slug-based routing is a future, low-risk
// addition once every published Team reliably has one.
//
// Fully public: no RequireAuth anywhere in this file. Every read goes
// through migration 038/041's public-safe RPCs; getPublicTeam() (038)
// and listPublicTeamGames() (041) both independently refuse to resolve
// anything for a Team whose public_page_enabled is false, regardless of
// how this URL was reached.

function GameRow({ game, variant }) {
  const isCompleted = variant === 'result';
  const result = isCompleted ? deriveGameResult(game.team_score, game.opponent_score) : null;
  // BROWSER ACCEPTANCE FOLLOW-UP (C.5d) — the lifecycle audit found this
  // page's own "upcoming" bucket has no date filter at all, so a past
  // scheduled Game with no result lingers here indefinitely, described
  // as if it were still upcoming. Rather than hiding it (it's still
  // real, public information — the audit's own principle is retention,
  // not disappearance) or changing which bucket it's fetched into, it's
  // labeled honestly: still 'scheduled', but its date has passed. No
  // private data involved — event_date/status are already public.
  const isPastAwaitingResult = !isCompleted && game.status === 'scheduled' && game.event_date < todayDateString();
  return (
    <Box sx={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.5, flexWrap: 'wrap',
      py: 1.25, px: 1.5, backgroundColor: 'rgba(10,20,34,0.8)', border: '1px solid #1e2a3a', borderRadius: 2,
    }}>
      <Box>
        <Typography sx={{ fontWeight: 700 }}>{formatGameOpponentLine(game)}</Typography>
        {game.location_name && !isCompleted && (
          <Typography sx={{ color: '#6a8aa8', fontSize: '0.78rem' }}>{game.location_name}</Typography>
        )}
      </Box>
      <Box sx={{ textAlign: 'right' }}>
        {isCompleted && result ? (
          <Typography sx={{ fontWeight: 800, color: result === 'W' ? '#2ea84b' : result === 'L' ? '#f44336' : '#9eb8d0' }}>
            {formatGameScoreLine(game)}
          </Typography>
        ) : game.status === 'postponed' ? (
          <Chip size="small" label="POSTPONED" sx={{ fontWeight: 800, ...gameStatusChipStyle('postponed') }} />
        ) : isPastAwaitingResult ? (
          <Chip size="small" label="AWAITING RESULT" sx={{ fontWeight: 800, bgcolor: '#2a1405', color: '#f5a623' }} />
        ) : null}
        <Typography sx={{ color: '#6a8aa8', fontSize: '0.78rem' }}>{formatGameDateCompact(game.event_date)}</Typography>
      </Box>
    </Box>
  );
}

function PlaceholderCard({ title, body }) {
  return (
    <Box sx={{ backgroundColor: 'rgba(8,16,28,0.5)', border: '1px solid #16202e', borderRadius: 2, p: 1.5 }}>
      <Typography sx={{ fontWeight: 700, fontSize: '0.8rem', color: '#9eb8d0', mb: 0.5 }}>{title}</Typography>
      <Typography sx={{ color: '#4a6080', fontSize: '0.75rem', lineHeight: 1.4 }}>{body}</Typography>
    </Box>
  );
}

export default function PublicTeamPage() {
  const { teamId } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [team, setTeam] = useState(null);
  const [division, setDivision] = useState(null);
  const [games, setGames] = useState([]);
  const [canManage, setCanManage] = useState(false);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setNotFound(false);
    setError(null);

    getPublicTeam(teamId).then(async (t) => {
      if (cancelled) return;
      if (!t) { setNotFound(true); setLoading(false); return; }
      setTeam(t);

      // Phase C.4 — listPublicTeamMatchups() (042) already returns rows
      // pre-oriented to this Team's own perspective in the SAME shape as
      // listPublicTeamGames() (opponent_name/home_away/team_score/
      // opponent_score) — merging needs no reshaping, and every
      // derivation below (record/next game/latest result) needs no
      // changes to include shared internal games correctly.
      const [div, teamGames, teamMatchups] = await Promise.all([
        getPublicDivision(t.division_id).catch(() => null),
        listPublicTeamGames(teamId).catch(() => []),
        listPublicTeamMatchups(teamId).catch(() => []),
      ]);
      if (cancelled) return;
      setDivision(div);
      setGames([...teamGames, ...teamMatchups]);
      setLoading(false);
    }).catch(e => {
      if (!cancelled) { setError(e.message); setLoading(false); }
    });

    return () => { cancelled = true; };
  }, [teamId]);

  // "Manage Schedule" is the ONLY authenticated-aware thing on this
  // otherwise fully public page — same "who can manage" shape as
  // TeamSchedulePage.js's own canManage (Team Admin/Head Coach via
  // listTeamStaff, or Program Owner/Admin via has_program_management_
  // authority_for_team). Both calls are RLS/authorization-safe to make
  // for any signed-in visitor: an unrelated user gets an empty staff
  // list and `false` back, never an error and never someone else's data.
  useEffect(() => {
    if (!currentUser || !team) { setCanManage(false); return; }
    let cancelled = false;
    Promise.all([
      listTeamStaff(team.id).catch(() => []),
      hasProgramManagementAuthorityForTeam(team.id).catch(() => false),
    ]).then(([staff, hasAuthority]) => {
      if (cancelled) return;
      const myRoles = staff.filter(s => s.user_id === currentUser.id && s.status === 'active').map(s => s.role);
      setCanManage(Boolean(hasAuthority || myRoles.includes('team_admin') || myRoles.includes('head_coach')));
    }).catch(() => { if (!cancelled) setCanManage(false); });
    return () => { cancelled = true; };
  }, [currentUser, team]);

  const upcoming = useMemo(
    () => games.filter(g => g.status === 'scheduled' || g.status === 'postponed'),
    [games]
  );
  const results = useMemo(
    () => [...games.filter(g => g.status === 'completed')].reverse(),
    [games]
  );
  const record = useMemo(() => deriveTeamRecord(games), [games]);
  const nextGame = upcoming[0] || null;
  const latestResult = results[0] || null;

  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh' }}>
        <Navbar />
        <Box sx={{ py: 10, textAlign: 'center' }}><CircularProgress sx={{ color: '#2ea84b' }} /></Box>
      </Box>
    );
  }

  if (notFound) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
        <Navbar />
        <Container maxWidth="sm" sx={{ py: 8, textAlign: 'center', flex: 1 }}>
          <Typography sx={{ fontSize: '2.2rem', mb: 1.5 }}>🏈</Typography>
          <Typography variant="h5" sx={{ fontWeight: 800, mb: 1 }}>Team page not found</Typography>
          <Typography sx={{ color: '#9eb8d0', mb: 3 }}>
            This team doesn't have a public page yet.
          </Typography>
          <Button variant="contained" color="primary" onClick={() => navigate('/programs')} sx={{ textTransform: 'none', fontWeight: 700 }}>
            Browse Programs
          </Button>
        </Container>
        <Footer />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ minHeight: '100vh' }}>
        <Navbar />
        <Container maxWidth="sm" sx={{ py: 8 }}><Alert severity="error">{error}</Alert></Container>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <Navbar />
      <Container maxWidth="sm" sx={{ py: { xs: 3, md: 4 }, flex: 1 }}>
        <Button onClick={() => navigate(`/programs/${team.program_slug}/divisions/${team.division_id}`)} sx={{ textTransform: 'none', color: '#6a8aa8', mb: 1, pl: 0 }}>
          ← {team.division_name}
        </Button>

        {/* ── Header ─────────────────────────────────────────────────── */}
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1.5, mb: 0.5 }}>
          <Typography variant="h4" sx={{ fontWeight: 900 }}>{team.team_name}</Typography>
          {canManage && (
            <Button
              size="small" variant="outlined"
              onClick={() => navigate(`/my-teams/${team.id}/schedule`)}
              sx={{ textTransform: 'none', fontWeight: 700, color: '#9eb8d0', borderColor: '#2a3a4e' }}
            >
              Manage Schedule
            </Button>
          )}
        </Box>
        <Typography
          sx={{ color: '#9eb8d0', cursor: 'pointer', display: 'inline-block' }}
          onClick={() => navigate(`/programs/${team.program_slug}`)}
        >
          {team.program_name}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mt: 0.25, mb: 3 }}>
          <Typography sx={{ color: '#6a8aa8', fontSize: '0.85rem' }}>
            {team.division_name} · {team.season_name} Season
          </Typography>
          {division?.competition_type && (
            <Chip size="small" label={competitionTypeLabel(division.competition_type)} sx={{ fontWeight: 700, ...competitionTypeChipStyle(division.competition_type) }} />
          )}
        </Box>

        {/* ── Team Summary ───────────────────────────────────────────── */}
        <Box sx={{
          display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: 1.25, mb: 4,
        }}>
          <Box sx={{ backgroundColor: 'rgba(10,20,34,0.8)', border: '1px solid #1e2a3a', borderRadius: 2, p: 1.5 }}>
            <Typography sx={{ color: '#6a8aa8', fontSize: '0.75rem', mb: 0.5 }}>RECORD</Typography>
            <Typography sx={{ fontWeight: 800 }}>{record.display || 'No results yet'}</Typography>
          </Box>
          <Box sx={{ backgroundColor: 'rgba(10,20,34,0.8)', border: '1px solid #1e2a3a', borderRadius: 2, p: 1.5 }}>
            <Typography sx={{ color: '#6a8aa8', fontSize: '0.75rem', mb: 0.5 }}>NEXT GAME</Typography>
            {nextGame ? (
              <>
                <Typography sx={{ fontWeight: 700 }}>{formatGameOpponentLine(nextGame)}</Typography>
                <Typography sx={{ color: '#6a8aa8', fontSize: '0.78rem' }}>{formatGameDateCompact(nextGame.event_date)}</Typography>
              </>
            ) : (
              <Typography sx={{ color: '#6a8aa8', fontSize: '0.85rem' }}>Not yet scheduled</Typography>
            )}
          </Box>
          <Box sx={{ backgroundColor: 'rgba(10,20,34,0.8)', border: '1px solid #1e2a3a', borderRadius: 2, p: 1.5 }}>
            <Typography sx={{ color: '#6a8aa8', fontSize: '0.75rem', mb: 0.5 }}>LATEST RESULT</Typography>
            {latestResult ? (
              <>
                <Typography sx={{ fontWeight: 700 }}>{formatGameOpponentLine(latestResult)}</Typography>
                <Typography sx={{ fontWeight: 800, color: deriveGameResult(latestResult.team_score, latestResult.opponent_score) === 'W' ? '#2ea84b' : '#f44336' }}>
                  {formatGameScoreLine(latestResult)}
                </Typography>
              </>
            ) : (
              <Typography sx={{ color: '#6a8aa8', fontSize: '0.85rem' }}>No results yet</Typography>
            )}
          </Box>
        </Box>

        {/* ── Schedule ───────────────────────────────────────────────── */}
        <Box sx={{ mb: 4 }}>
          <Typography sx={{ fontWeight: 800, fontSize: '0.85rem', letterSpacing: '0.04em', color: '#e8f0f8', mb: 1.5 }}>
            SCHEDULE
          </Typography>
          {upcoming.length === 0 ? (
            <Typography sx={{ color: '#6a8aa8', fontSize: '0.85rem' }}>No upcoming games scheduled.</Typography>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {upcoming.map(g => <GameRow key={g.id} game={g} variant="upcoming" />)}
            </Box>
          )}
        </Box>

        {/* ── Latest Results ─────────────────────────────────────────── */}
        <Box sx={{ mb: 4 }}>
          <Typography sx={{ fontWeight: 800, fontSize: '0.85rem', letterSpacing: '0.04em', color: '#e8f0f8', mb: 1.5 }}>
            LATEST RESULTS
          </Typography>
          {results.length === 0 ? (
            <Typography sx={{ color: '#6a8aa8', fontSize: '0.85rem' }}>No results yet.</Typography>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {results.map(g => <GameRow key={g.id} game={g} variant="result" />)}
            </Box>
          )}
        </Box>

        {/* ── Future module shells — no fabricated data ─────────────── */}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' }, gap: 1.25 }}>
          <PlaceholderCard title="Team Highlights" body="Highlights will appear here as they're shared." />
          <PlaceholderCard title="Development" body="Development updates will appear here as they're shared." />
        </Box>
      </Container>
      <Footer />
    </Box>
  );
}
